import {
  BidInviteActionRoute,
  BidPackageListTabs,
  CompanySettingsTabs,
  type generateFrontendRoutes,
  ProjectListTabs,
  UserProfileTabs,
} from '@cosuno/routes';
import { queryStringToObject } from '@cosuno/utils';
import React, { type PropsWithChildren, useMemo } from 'react';

import PageNotFound from '~/components/PageNotFound';

import AuthSwitch from './AuthSwitch';
import NavigateWithParams from './NavigateWithParams';
import OutletWithPageLoader from './OutletWithPageLoader';
import RedirectToBaseUrl from './RedirectToBaseUrl';
import type { ExtendedRouteObject } from './types';
import { C, CD, mapPathToHandle } from './utils';

const allowAgent = true;
const allowPrincipal = true;
const allowAnonymous = true;
const allowAuthenticated = true;
const allowCompanyAdmin = true;

const useRouteConfig = (
  Page: React.ComponentType<PropsWithChildren<{ renderLayout?: boolean }>>,
  routes: ReturnType<typeof generateFrontendRoutes>,
): ExtendedRouteObject[] =>
  useMemo(
    () =>
      mapPathToHandle([
        // layout route
        {
          id: 'layout',
          element: (
            <Page renderLayout key="layout">
              <OutletWithPageLoader />
            </Page>
          ),
          handle: {},
          children: [
            // layout routes for agent users
            {
              path: routes.agentBidRequests(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentBidRequests" */ '~/components/AgentBidRequests'
                  ),
                ),
            },
            {
              path: routes.agentCreateBidRequest(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentBidRequest" */ '~/components/AgentBidRequest/CreateBidRequest'
                  ),
                ),
            },
            {
              path: routes.agentEditBidRequest(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentBidRequest" */ '~/components/AgentBidRequest/EditBidRequest'
                  ),
                ),
            },
            {
              path: routes.agentEditBidRequestImportBoQ(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentBidRequest" */ '~/components/AgentBidRequest/EditBidRequestImportBoQ'
                  ),
                ),
            },
            {
              path: `${routes.agentCompanyOnboarding()}/*`,
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentCompanyOnboarding" */ '~/components/AgentCompanyOnboarding'
                  ),
                ),
            },
            {
              path: routes.agentAwardedBid(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(/* webpackChunkName: "AgentAwardedBid" */ '~/components/AgentAwardedBid'),
                ),
            },
            {
              path: `${routes.agentCertificates()}/*`,
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentCertificates" */ '~/components/AgentCertificates'
                  ),
                ),
            },
            {
              path: routes.agentPublicCertificates(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentPublicCertificates" */ '~/components/AgentPublicCertificates'
                  ),
                ),
            },
            {
              path: routes.agentCompleteRegistration(),
              handle: { allowAgent },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentCompleteRegistration" */ '~/components/AgentCompleteRegistration/AgentCompleteRegistration'
                  ),
                ),
            },
            {
              path: routes.reverseMarketplaceBidPackage(),
              handle: {},
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "BidRequestLandingPage" */ '~/components/BidRequestLandingPage/ReverseMarketplace'
                  ),
                  'ReverseMarketplaceBidPackage',
                ),
            },
            {
              path: routes.agentReverseMarketplace(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "ReverseMarketplace" */ '~/components/ReverseMarketplace'
                  ),
                  'ReverseMarketplaceRoute',
                ),
            },
            {
              path: routes.premium(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(import(/* webpackChunkName: "AgentPremium" */ '~/components/AgentPremium')),
            },
            {
              path: routes.premium(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(/* webpackChunkName: "AgentPremium" */ '~/components/AgentPremiumRenewal'),
                ),
            },
            ...Object.values(BidInviteActionRoute).map((action) => ({
              path: routes.bidInviteAction(':bidInviteId', action),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "BidInviteFeedback" */ '~/components/AgentUnauthenticated/BidInviteFeedback'
                  ),
                ),
            })),
            {
              path: `${routes.bidInvite()}/*`,
              handle: {},
              lazy: () => CD(import(/* webpackChunkName: "BidInvite" */ '~/components/BidInvite')),
            },
            {
              path: routes.bidRequestLandingPage(),
              handle: { allowAgent, allowAnonymous, navigationUiState: 'collapsed' },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "BidRequestLandingPage" */ '~/components/BidRequestLandingPage'
                  ),
                  'BidRequestLandingPage',
                ),
            },
            {
              path: routes.subcontractorCreateBid(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "SubcontractorBid" */ '~/components/SubcontractorBid'
                  ),
                  'SubcontractorBidCreationPage',
                ),
            },
            {
              path: routes.subcontractorBid(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "SubcontractorBid" */ '~/components/SubcontractorBid'
                  ),
                  'SubcontractorBidPage',
                ),
            },
            {
              path: routes.subcontractorCertificateRequest(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "UploadCertificates" */ '~/components/UploadCertificates'
                  ),
                ),
            },
            {
              path: routes.uploadCertificates(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "UploadCertificates" */ '~/components/UploadCertificates'
                  ),
                ),
            },
            // layout routes for principal users
            {
              path: routes.invoices(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Invoices" */ '~/components/Invoices')),
            },
            {
              path: routes.createInvoice(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Invoices" */ '~/components/Invoices/Create')),
            },
            {
              path: routes.createBidPackageInvoice(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Invoices" */ '~/components/Invoices/Create')),
            },
            {
              path: `${routes.invoice()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Invoices" */ '~/components/Invoices/View')),
            },
            {
              path: `${routes.bidPackageInvoice()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Invoices" */ '~/components/Invoices/View')),
            },
            {
              path: routes.addendums(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Addendums" */ '~/components/Addendums')),
            },
            {
              path: routes.createAddendum(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Addendums" */ '~/components/Addendums/Create')),
            },
            {
              path: routes.createBidPackageAddendum(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Addendums" */ '~/components/Addendums/Create')),
            },
            {
              path: routes.bidPackageAddendum(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Addendums" */ '~/components/Addendums/View')),
            },
            ...Object.values(ProjectListTabs).map((tab) => ({
              path: routes.projects(tab),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Projects" */ '~/components/Projects')),
            })),
            {
              path: routes.projects(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Projects" */ '~/components/Projects')),
            },
            {
              path: routes.awardBid(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "AwardBid" */ '~/components/AwardedBid/AwardBid')),
            },
            {
              path: `${routes.createProject()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "Project" */ '~/components/Project'),
                  'CreateProject',
                ),
            },
            {
              path: routes.projectBimDocument(),
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "Project" */ '~/components/Project'),
                  'ProjectBimDocument',
                ),
            },
            {
              path: `${routes.showProject()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                C(import(/* webpackChunkName: "Project" */ '~/components/Project'), 'ShowProject'),
            },
            {
              path: routes.bids(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Bids" */ '~/components/Bids')),
            },
            {
              path: routes.submitBid(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Bid" */ '~/components/Bid/SubmitBid')),
            },
            {
              path: routes.showBid(),
              handle: { allowPrincipal },
              lazy: () => CD(import(/* webpackChunkName: "Bid" */ '~/components/Bid/ShowBid')),
            },
            {
              path: routes.showAwardedBid(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AwardedBid" */ '~/components/AwardedBid/ShowAwardedBid'
                  ),
                ),
            },
            ...Object.values(BidPackageListTabs).map((tab) => ({
              path: routes.bidPackages(tab),
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "BidPackages" */ '~/components/BidPackages'),
                  'BidPackages',
                ),
            })),
            {
              path: routes.bidPackages(),
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "BidPackages" */ '~/components/BidPackages'),
                  'BidPackages',
                ),
            },
            {
              path: `${routes.showBidPackage()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "BidPackage" */ '~/components/BidPackage/ShowBidPackage'
                  ),
                ),
            },
            {
              path: routes.costComparison(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "BidPackage" */ '~/components/BidPackage/CostComparison'
                  ),
                ),
            },
            {
              path: routes.publishBidPackage(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "BidPackage" */ '~/components/BidPackage/PublishBidPackage'
                  ),
                ),
            },
            {
              path: routes.createBidPackage(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "BidPackage" */ '~/components/BidPackage/CreateBidPackage'
                  ),
                ),
            },
            {
              path: `${routes.internalDocuments()}/*`,
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "InternalDocuments" */ '~/components/InternalDocuments'
                  ),
                  'InternalDocuments',
                ),
            },
            {
              path: routes.createBoQTemplate(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "CreateBoQTemplate" */ '~/components/InternalDocuments/BoQLibrary/CreateBoQTemplate'
                  ),
                ),
            },
            {
              path: routes.showBoQTemplate(),
              handle: { allowPrincipal },
              lazy: () =>
                C(import(/* webpackChunkName: "BoQTemplate" */ '~/packages/BoQ'), 'BoQTemplate'),
            },

            {
              path: routes.subcontractors(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(import(/* webpackChunkName: "Subcontractors" */ '~/components/Subcontractors')),
            },
            {
              path: routes.newSubcontractor(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "SubcontractorProfile" */ '~/shared/components/SubcontractorProfile/Create'
                  ),
                ),
            },
            {
              path: routes.subcontractor(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "SubcontractorProfile" */ '~/shared/components/SubcontractorProfile/Show'
                  ),
                ),
            },
            {
              path: routes.goldPackageOverview(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "GoldPackageOverview" */ '~/components/GoldPackageOverview'
                  ),
                ),
            },

            // layout routes for all users
            {
              path: `${routes.profile()}/*`,
              handle: { allowAuthenticated },
              lazy: () => CD(import(/* webpackChunkName: "Profile" */ '~/components/Profile')),
            },
            {
              path: `${routes.companyOnboarding()}/*`,
              handle: { allowAuthenticated },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "CompanyOnboarding" */ '~/components/CompanyOnboarding'
                  ),
                ),
            },
            {
              path: routes.showCompanyRole('new'),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/Permissions/CreateRolePage'
                  ),
                ),
            },
            {
              path: routes.showCompanyRole(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/Permissions/RolePage'
                  ),
                ),
            },
            {
              path: routes.newOffice(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(import(/* webpackChunkName: "Office" */ '~/components/Office/CreateOffice')),
            },
            {
              path: routes.office(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(import(/* webpackChunkName: "Office" */ '~/components/Office/EditOffice')),
            },
            {
              path: routes.multiUserApprovalRules(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/MultiUserApproval'
                  ),
                  'MultiUserApprovalRules',
                ),
            },
            {
              path: routes.createCustomCostGroupCatalog(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/CustomCatalogs/CreateCustomCostGroupCatalog'
                  ),
                ),
            },
            {
              path: routes.editCustomCostGroupCatalog(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/CustomCatalogs/EditCustomCostGroupCatalog'
                  ),
                ),
            },
            {
              path: routes.showCustomCostGroupCatalog(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/CustomCatalogs/ShowCustomCostGroupCatalog'
                  ),
                ),
            },
            {
              path: routes.companyCustomFieldsForEntityType(),
              handle: { allowCompanyAdmin },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/CustomFields/CustomFieldsForEntityType'
                  ),
                ),
            },
            {
              path: `${routes.company()}/*`,
              handle: { allowAuthenticated },
              lazy: () => CD(import(/* webpackChunkName: "Company" */ '~/components/Company')),
            },
            {
              path: routes.messages(),
              handle: { allowAuthenticated },
              lazy: () => CD(import(/* webpackChunkName: "Messages" */ '~/components/Messages')),
            },
            {
              path: routes.expiredProjectDownloadLink(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Project" */ '~/components/Project/ExpiredProjectDownloadLink'
                  ),
                ),
            },
            {
              path: routes.subcontractorReferenceProject(),
              handle: {},
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "SubcontractorReferenceProject" */ '~/components/SubcontractorReferenceProject'
                  ),
                  'SubcontractorReferenceProject',
                ),
            },
            {
              path: routes.tasks(),
              handle: { allowAuthenticated },
              lazy: () => CD(import(/* webpackChunkName: "Tasks" */ '~/components/Tasks')),
            },
            {
              path: routes.createTask(),
              handle: { allowAuthenticated },
              lazy: () =>
                CD(import(/* webpackChunkName: "Tasks" */ '~/components/Tasks/Task/CreateTask')),
            },
            {
              path: routes.showTask(),
              handle: { allowAuthenticated },
              lazy: () =>
                CD(import(/* webpackChunkName: "Tasks" */ '~/components/Tasks/Task/ViewTask')),
            },
          ],
        },
        // non layout route
        {
          id: 'non-layout',
          element: (
            <Page key="non-layout">
              <OutletWithPageLoader />
            </Page>
          ),
          handle: {},
          children: [
            // routes without layout for agent users
            {
              path: routes.agentSignContract(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(/* webpackChunkName: "AgentAwardedBid" */ '~/components/AgentAwardedBid'),
                ),
            },
            {
              path: routes.reverseMarketplaceBidPackageAcceptTerms(),
              handle: { allowAgent, allowAnonymous, navigationUiState: 'collapsed' },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "Terms" */ '~/components/Terms/TermsRoute'),
                  'TermsRoute',
                ),
            },
            {
              path: routes.bidRequestAcceptTerms(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                C(
                  import(/* webpackChunkName: "Terms" */ '~/components/Terms/TermsRoute'),
                  'TermsRoute',
                ),
            },
            {
              path: routes.agentReverseMarketplaceSuggestion(),
              handle: { allowAgent, allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "MarketplaceSuggestion" */ '~/components/MarketplaceSuggestion'
                  ),
                ),
            },
            // routes without layout for principal users
            {
              path: routes.previewBidRequestLandingPage(),
              handle: { allowPrincipal },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "BidRequestLandingPage" */ '~/components/BidRequestLandingPage/PreviewBidRequestLandingPage'
                  ),
                  'PreviewBidRequestLandingPage',
                ),
            },
            {
              path: routes.subcontractorExternalBid(),
              handle: { allowPrincipal, allowAnonymous },
              lazy: () =>
                C(
                  import(
                    /* webpackChunkName: "SubcontractorBid" */ '~/components/SubcontractorBid'
                  ),
                  'SubcontractorBidExternalViewPage',
                ),
            },
            {
              path: routes.publishMarketplaceRestrictedView(),
              handle: { allowPrincipal },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "PublishMarketplaceRestrictedView" */ '~/components/PublishMarketplaceRestrictedView'
                  ),
                ),
            },
            // routes without layout for authenticated users
            {
              path: routes.mfaInitialSetup(),
              handle: { allowAuthenticated },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "MfaSetup" */ '~/components/Auth/MfaSetup/MfaInitialSetup'
                  ),
                ),
            },
            {
              path: routes.mfaSetup(),
              handle: { allowAuthenticated },
              lazy: () =>
                CD(import(/* webpackChunkName: "MfaSetup" */ '~/components/Auth/MfaSetup')),
            },
            // routes without layout for anonymous users
            {
              path: routes.login(),
              handle: { allowAnonymous },
              lazy: () =>
                CD(import(/* webpackChunkName: "LoginPage" */ '~/components/Auth/LoginPage')),
            },
            {
              path: routes.resetPassword(),
              handle: { allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "ResetPasswordPage" */ '~/components/Auth/ResetPasswordPage'
                  ),
                ),
            },
            {
              path: routes.createPassword(),
              handle: { allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "CreatePassword" */ '~/components/Auth/CreatePassword'
                  ),
                ),
            },
            {
              path: routes.userLocked(),
              handle: { allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "UserLockedPage" */ '~/components/Auth/UserLockedPage'
                  ),
                ),
            },
            {
              path: routes.loginWithSSO(),
              handle: { allowAnonymous },
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "LoginWithSSOPage" */ '~/components/Auth/LoginWithSSOPage'
                  ),
                ),
            },
            // routes without layout for all users
            {
              path: routes.ssoError(),
              handle: {},
              lazy: () =>
                CD(import(/* webpackChunkName: "SsoErrorPage" */ '~/components/Auth/SsoErrorPage')),
            },
            {
              path: routes.closeDocuSignModal(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "CloseDocuSignModal" */ '~/components/CloseDocuSignModal'
                  ),
                ),
            },
            {
              path: routes.invitation(),
              handle: {},
              lazy: () =>
                CD(import(/* webpackChunkName: "Invitation" */ '~/components/Auth/Invitation')),
            },
            {
              path: routes.confirmInviteRequest(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "ConfirmInviteRequest" */ '~/components/Auth/ConfirmInviteRequest'
                  ),
                ),
            },
            {
              path: routes.taskLogin(),
              handle: {},
              lazy: () =>
                CD(import(/* webpackChunkName: "TaskLogin" */ '~/components/Tasks/Task/AuthPage')),
            },
            {
              path: routes.agentUnsolicitedSignup(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentUnsolicitedSignupPage" */ '~/components/Auth/AgentUnsolicitedSignupPage/AgentUnsolicitedSignupPage'
                  ),
                ),
            },
            {
              path: routes.agentUnauthenticatedUpdateProfileBySubcontractor(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentUnauthenticatedUpdateProfile" */ '~/components/AgentUnauthenticated/UpdateProfile/BySubcontractor'
                  ),
                ),
            },
            {
              path: routes.agentUnauthenticatedUpdateProfileByBidRequest(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentUnauthenticatedUpdateProfile" */ '~/components/AgentUnauthenticated/UpdateProfile/ByBidRequest'
                  ),
                ),
            },
            {
              path: routes.agentUnauthenticatedMarketplaceSamples(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "AgentUnauthenticatedMarketplaceSamples" */ '~/components/AgentUnauthenticated/MarketplaceSamples'
                  ),
                ),
            },
            {
              path: routes.agentPremiumSuccess(),
              handle: {},
              lazy: () =>
                CD(
                  import(
                    /* webpackChunkName: "Company" */ '~/components/Company/AgentPremiumSuccess'
                  ),
                ),
            },
            {
              path: routes.redirect(),
              handle: {},
              lazy: () =>
                CD(import(/* webpackChunkName: "Redirecting" */ '~/components/Redirecting')),
            },
          ],
        },
        /** Keep old custom project fields link working */
        {
          path: routes.company({ tab: CompanySettingsTabs.customProjectFields }),
          handle: {},
          element: <NavigateWithParams to={routes.companyCustomFieldsFor_project()} />,
        },
        /** Keep old email templates link working */
        {
          path: routes.company({ tab: CompanySettingsTabs.emailTemplates }),
          handle: {},
          element: (
            <NavigateWithParams to={routes.company({ tab: CompanySettingsTabs.templates })} />
          ),
        },
        {
          path: routes.company({ tab: CompanySettingsTabs.premium }),
          handle: {},
          element: (
            <NavigateWithParams to={routes.premium(queryStringToObject(window.location.search))} />
          ),
        },
        {
          path: routes.company({ tab: CompanySettingsTabs.notifications }),
          handle: {},
          element: <NavigateWithParams to={routes.profile(UserProfileTabs.notifications)} />,
        },
        {
          path: routes.company({ tab: CompanySettingsTabs.premium, skipWorkspace: true }),
          handle: {},
          element: (
            <NavigateWithParams to={routes.premium(queryStringToObject(window.location.search))} />
          ),
        },
        /** This route is only a fix for suggestion emails sent at 2024-01-08 */
        {
          path: routes.agentReverseMarketplaceSuggestionRedirect(),
          handle: {},
          lazy: () =>
            C(
              import('~/components/MarketplaceSuggestion/MarketplaceSuggestionRedirect'),
              'MarketplaceSuggestionRedirect',
            ),
        },
        {
          path: routes.tasksRoot(),
          handle: {},
          element: <NavigateWithParams to={routes.tasks()} replace />,
        },
        {
          path: routes.myTasks(),
          handle: {},
          element: <NavigateWithParams to={routes.tasks()} state={{ showMyTasks: true }} replace />,
        },
        {
          path: `${routes.subcontractorsOld()}/*`,
          handle: {},
          element: <NavigateWithParams to={routes.subcontractors()} replace />,
        },
        {
          id: 'page-not-found',
          path: '*',
          handle: {},
          element: (
            <AuthSwitch
              authenticatedElement={
                <Page renderLayout>
                  <PageNotFound.Authenticated />
                </Page>
              }
              unauthenticatedElement={
                <Page>
                  <PageNotFound.Unauthenticated />
                </Page>
              }
            />
          ),
        },
        {
          index: true,
          element: <RedirectToBaseUrl />,
          handle: {},
        },
      ]),
    [Page, routes],
  );

export default useRouteConfig;
